import { AxiosError } from 'axios';
import api from '../api';
import { ResultadoFinal, Votacao } from '../model/banca/votacao/Votacao';
import { Voto, VotoCandidato } from '../model/banca/votacao/Voto';

interface ConsultaVotacaoResponse {
    candidatoBanca: {
        candidatoId: string;
        nome: string;
        nomeSocial: string;
        inscricao: string;
        statusCandidato: string; // Updated to string to handle raw data
        resultado: string; // Updated to string to handle raw data
    };
    votos: {
        membro: {
            id: string;
            pessoa: {
                id: string;
                nome: string;
                cpf: string;
                vinculo: {
                    id: string;
                    nome: string;
                };
                email: string;
                matricula: string;
            };
            papelMembro: {
                id: string;
                nome: string; // Updated to string to handle raw data
            };
            statusMembro: string;
        };
        voto: string; // Updated to string to handle raw data
        justificativa: string;
        dataHoraVotacao: string;
    }[];
}


export default class VotacaoViewModel {

    static async patchFecharVotacao(bancaId: string, candidatoId: string): Promise<any> {
        try {
            const response = await api.patch(`/bancas/${bancaId}/candidatos/${candidatoId}/votacao/fechamento`);
            return response; // Retorna a resposta da requisição
        } catch (error) {
            console.error('Erro ao fechar Heteroidentificação:', error);
            throw error;
        }
    }

    static async patchAbrirVotacao(bancaId: string, candidatoId: string): Promise<any> {
        try {
            const response = await api.patch(`/bancas/${bancaId}/candidatos/${candidatoId}/votacao/abertura`);
            return response; // Retorna a resposta da requisição
        } catch (error) {
            console.error('Erro ao abrir Heteroidentificação:', error);
            throw error;
        }
    }


    static async postVoto(bancaId: string, candidatoId: string, voto: Voto): Promise<Voto> {
        const votoFormatado = this.mapResultadoToApi(voto.resultado);
        try {            
            const response = await api.post(`/bancas/${bancaId}/candidatos/${candidatoId}/voto`, {
                voto: votoFormatado,
                justificativa: voto.justificativa
            });
    
            // Mapeamento da resposta para o tipo Voto
            const votoMapeado: Voto = {
                id: response.data.id,
                votante: voto.votante,  // Presumindo que o votante é enviado corretamente
                resultado: this.mapResultadoToEnum(response.data.voto),
                justificativa: response.data.justificativa,
                data: new Date(response.data.data)
            };
    
            return votoMapeado;
        } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        console.error('Bad Request:', error.response.data);
                        break;
                    case 404:
                        console.error('Not Found:', error.response.data);
                        break;
                    case 500:
                        console.error('Internal Server Error:', error.response.data);
                        break;
                    default:
                        console.error('Erro desconhecido:', error.response.data);
                }
            } else {
                console.error('Erro ao enviar voto:', error);
            }
            throw error;
        }
    }
    

    // Função auxiliar para mapear resultado string para enum VotoCandidato
    private static mapResultadoToEnum(resultado: string): VotoCandidato {
        switch (resultado) {
            case "VALIDADO":
                return VotoCandidato.VALIDADO;
            case "INCONCLUSIVO":
                return VotoCandidato.INCONCLUSIVO;
            case "NAO_VALIDADO":
                return VotoCandidato.NAO_VALIDADO;
            default:
                return VotoCandidato.NAO_VALIDADO;
        }
    }

    private static mapResultadoToApi(resultado: VotoCandidato): string {
        switch (resultado) {
            case VotoCandidato.VALIDADO:
                return 'VALIDADO';
            case VotoCandidato.INCONCLUSIVO:
                return 'INCONCLUSIVO';
            case VotoCandidato.NAO_VALIDADO:
                return 'NAO_VALIDADO';
            default:
                return 'NAO_VALIDADO';
        }
    }

    static async getVotacoesByBanca(bancaId: string): Promise<Votacao[]> {
        try {
            const response = await api.get(`/bancas/${bancaId}/votacoes`);
            const votacoes = response.data.map((data: any) => ({
                id: data.id,
                candidato: data.candidato, // Assumindo que o mapeamento do candidato já esteja correto
                votos: data.votos.map((voto: any) => ({
                    id: voto.id,
                    votante: voto.votante,
                    resultado: this.mapResultadoToEnum(voto.resultado),
                    justificativa: voto.justificativa,
                    data: new Date(voto.data)
                })),
                dataInicio: data.dataInicio ? new Date(data.dataInicio) : undefined,
                dataFim: data.dataFim ? new Date(data.dataFim) : undefined,
                status: data.status,
                resultado: data.resultado ? this.mapResultadoFinalToEnum(data.resultado) : null
            }));
            return votacoes;
        } catch (error) {
            console.error('Erro ao buscar votações por banca:', error);
            throw error;
        }
    }

    // Função auxiliar para mapear resultado final string para enum ResultadoFinal
    private static mapResultadoFinalToEnum(resultado: string): ResultadoFinal {
        switch (resultado) {
            case "VALIDADO":
                return ResultadoFinal.VALIDADO;
            case "NAO_VALIDADO":
                return ResultadoFinal.NAO_VALIDADO;
            case "AUSENTE":
                return ResultadoFinal.AUSENTE;
            default:
                return ResultadoFinal.AUSENTE;
        }
    }

}
